import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useWax } from '../context/WaxContext';

const TW = 32;
const TH = 16;
const COLS = 100;
const ROWS = 100;
const TILE_H = 3;

const SPECIES_EMOJI: Record<string, string> = {
  amaranth: '🌱', vetiver: '🌾', opuntia: '🌵', moringa: '🌿',
  jujube: '🫐', acacia: '🌳', mesquite: '🌲', neem: '🌴',
};
const SPECIES_MATURITY_H: Record<string, number> = {
  amaranth: 4, vetiver: 8, opuntia: 16, moringa: 24,
  jujube: 36, acacia: 48, mesquite: 72, neem: 120,
};

const OWNERS = [
  { name: 'lonestar1234',  plots: 6646, top: '#d97706', s: '#78350f', e: '#5f2a0c' },
  { name: 'amerharki123',  plots: 600,  top: '#fb923c', s: '#c2410c', e: '#9c340a' },
  { name: 'x.1my.c.wam',  plots: 500,  top: '#2dd4bf', s: '#0f766e', e: '#0c5e58' },
  { name: 'dreamingpal1',  plots: 500,  top: '#f43f5e', s: '#9f1239', e: '#7d0e2d' },
  { name: 'w4dba.wam',     plots: 400,  top: '#34d399', s: '#065f46', e: '#044a38' },
  { name: 'maulbaer12de',  plots: 400,  top: '#a78bfa', s: '#5b21b6', e: '#4a1a94' },
  { name: 'w3mz2.wam',     plots: 100,  top: '#60a5fa', s: '#1d4ed8', e: '#163caa' },
  { name: 'lk5g.wam',      plots: 250,  top: '#818cf8', s: '#3730a3', e: '#2d2882' },
  { name: 'rudrikwax.gm',  plots: 126,  top: '#38bdf8', s: '#0e7490', e: '#0b5c72' },
  { name: 'ahantravh.gm',  plots: 99,   top: '#f97316', s: '#c2570a', e: '#9a430a' },
  { name: 'ahn.gm',        plots: 78,   top: '#84cc16', s: '#3f6212', e: '#315010' },
  { name: 'akshatvyu.gm',  plots: 70,   top: '#f472b6', s: '#9d174d', e: '#7c133c' },
  { name: 'tixw2.wam',     plots: 50,   top: '#fbbf24', s: '#b45309', e: '#904208' },
  { name: 'b4nwa.wam',     plots: 50,   top: '#4ade80', s: '#15803d', e: '#105c2d' },
  { name: 'beustapipout',  plots: 20,   top: '#fca5a5', s: '#b91c1c', e: '#941616' },
  { name: 'rudra.gm',      plots: 13,   top: '#ef4444', s: '#991b1b', e: '#7c1616' },
  { name: '42.am.c.wam',   plots: 12,   top: '#c084fc', s: '#7e22ce', e: '#631c9e' },
  { name: 's4ssm.wam',     plots: 11,   top: '#6ee7b7', s: '#065f46', e: '#054a38' },
  { name: 'psycryptosku',  plots: 11,   top: '#fdba74', s: '#c2410c', e: '#9c340a' },
  { name: 's.4bi.wam',     plots: 10,   top: '#e879f9', s: '#86198f', e: '#6a1472' },
  { name: 'su.b2.wam',     plots: 10,   top: '#c4b5fd', s: '#6d28d9', e: '#5720b0' },
  { name: 'rua3e.c.wam',   plots: 10,   top: '#7dd3fc', s: '#0369a1', e: '#025480' },
  { name: 'bigblvd.gm',    plots: 10,   top: '#d4a574', s: '#92400e', e: '#743308' },
  { name: 'dlpqa.c.wam',   plots: 4,    top: '#fde047', s: '#a16207', e: '#845004' },
];

// Fill n cells of owner idx into map, row-major from (bx,by) in W×H block
function fillBlock(map: Map<string, number>, idx: number, bx: number, by: number, W: number, H: number, n: number) {
  let k = 0;
  for (let r = by; r < by + H && k < n; r++)
    for (let c = bx; c < bx + W && k < n; c++) {
      map.set(`${c},${r}`, idx);
      k++;
    }
}


function drawTile(
  ctx: CanvasRenderingContext2D,
  col: number, row: number,
  top: string, south: string, east: string,
) {
  const cx = (col - row) * TW / 2;
  const gy = (col + row) * TH / 2;
  const ty = gy - TILE_H;

  ctx.beginPath();
  ctx.moveTo(cx - TW / 2, ty); ctx.lineTo(cx, ty + TH / 2);
  ctx.lineTo(cx, gy + TH / 2); ctx.lineTo(cx - TW / 2, gy);
  ctx.closePath(); ctx.fillStyle = south; ctx.fill();

  ctx.beginPath();
  ctx.moveTo(cx + TW / 2, ty); ctx.lineTo(cx, ty + TH / 2);
  ctx.lineTo(cx, gy + TH / 2); ctx.lineTo(cx + TW / 2, gy);
  ctx.closePath(); ctx.fillStyle = east; ctx.fill();

  ctx.beginPath();
  ctx.moveTo(cx, ty - TH / 2); ctx.lineTo(cx + TW / 2, ty);
  ctx.lineTo(cx, ty + TH / 2); ctx.lineTo(cx - TW / 2, ty);
  ctx.closePath(); ctx.fillStyle = top; ctx.fill();
}

const ATAVI_LOCKED = true; // reset in progress — re-enable by setting to false

const AtaviForestPage: React.FC<{ onBack: () => void; onOpenAtaviAssets: () => void }> = ({ onBack, onOpenAtaviAssets }) => {

  // ── Owner map: hardcoded rectangular placements ──
  // Layout:
  //   Rows  0-24 : big owners (amerharki, x.1my, dreamingpal, w4dba, maulbaer, w3mz2)
  //   Rows 25-40 : medium owners (lk5g, rudrikwax, ahantravh, ahn, akshatvyu, tixw2, b4nwa, beustapipout, rudra, 42.am, s4ssm, psycryptosku, s.4bi, su.b2, rua3e, bigblvd, dlpqa)
  //   Remainder  : lonestar1234 fills all remaining cells (continuous block rows 41-99)
  const ownerMap = useMemo(() => {
    const map = new Map<string, number>();

    // ── Rows 0-24: big owners, each spanning full row height ──
    fillBlock(map,  1,  0,  0, 24, 25, 600);  // amerharki123  24×25=600
    fillBlock(map,  2, 24,  0, 20, 25, 500);  // x.1my.c.wam   20×25=500
    fillBlock(map,  3, 44,  0, 20, 25, 500);  // dreamingpal1   20×25=500
    fillBlock(map,  4, 64,  0, 16, 25, 400);  // w4dba.wam      16×25=400
    fillBlock(map,  5, 80,  0, 20, 20, 400);  // maulbaer12de   20×20=400
    fillBlock(map,  6, 80, 20, 20,  5, 100);  // w3mz2.wam      20×5=100

    // ── Rows 25+: medium owners, near-square blocks ──
    fillBlock(map,  7,  0, 25, 16, 16, 250);  // lk5g.wam       16×16 fill 250
    fillBlock(map,  8, 16, 25, 12, 11, 126);  // rudrikwax.gm   12×11 fill 126
    fillBlock(map,  9, 28, 25, 10, 10,  99);  // ahantravh.gm   10×10 fill 99
    fillBlock(map, 10, 38, 25,  9,  9,  78);  // ahn.gm          9×9  fill 78
    fillBlock(map, 11, 47, 25,  9,  8,  70);  // akshatvyu.gm    9×8  fill 70
    fillBlock(map, 12, 56, 25,  8,  7,  50);  // tixw2.wam       8×7  fill 50
    fillBlock(map, 13, 64, 25,  8,  7,  50);  // b4nwa.wam       8×7  fill 50
    fillBlock(map, 14, 72, 25,  5,  4,  20);  // beustapipout    5×4=20
    fillBlock(map, 15, 77, 25,  4,  4,  13);  // rudra.gm        4×4  fill 13
    fillBlock(map, 16, 81, 25,  4,  3,  12);  // 42.am.c.wam    4×3=12
    fillBlock(map, 17, 85, 25,  4,  3,  11);  // s4ssm.wam       4×3  fill 11
    fillBlock(map, 18, 89, 25,  4,  3,  11);  // psycryptosku    4×3  fill 11
    fillBlock(map, 19, 93, 25,  4,  3,  10);  // s.4bi.wam       4×3  fill 10
    fillBlock(map, 20, 97, 25,  3,  4,  10);  // su.b2.wam       3×4  fill 10
    fillBlock(map, 21, 78, 28,  3,  4,  10);  // rua3e.c.wam     3×4  fill 10 (covers gap at 78-80,r=28)
    fillBlock(map, 22, 81, 28,  4,  3,  10);  // bigblvd.gm      4×3  fill 10
    fillBlock(map, 23, 85, 28,  2,  2,   4);  // dlpqa.c.wam    2×2=4

    // ── Lonestar: fills all remaining cells except epicenter ──
    for (let r = 0; r < ROWS; r++)
      for (let c = 0; c < COLS; c++) {
        const key = `${c},${r}`;
        const isEpicenter = c >= 47 && c <= 51 && r >= 48 && r <= 51;
        if (!map.has(key) && !isEpicenter) map.set(key, 0);
      }

    return map;
  }, []);

  // ── WAX session — from global context ──
  const { waxActor, waxSession } = useWax();
  const [harvestBusy, setHarvestBusy] = useState(false);
  const [harvestMsg,  setHarvestMsg]  = useState('');
  const [showStakePanel, setShowStakePanel] = useState(false);
  const [nftList,        setNftList]        = useState<any[]>([]);
  const [nftLoading,     setNftLoading]     = useState(false);
  const [selectedNft,    setSelectedNft]    = useState<any>(null);
  const [previewView,    setPreviewView]    = useState(false);
  const [landCount,      setLandCount]      = useState(0);
  const [plotsToLoad,    setPlotsToLoad]    = useState(0);
  const [foodScore,      setFoodScore]      = useState(0);
  const [maxPackets,     setMaxPackets]     = useState(0);
  const [packetsToLoad,  setPacketsToLoad]  = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [staking,        setStaking]        = useState(false);
  const [stakeMsg,       setStakeMsg]       = useState('');
  const [showNftModal,   setShowNftModal]   = useState(false);
  const [myStakes,       setMyStakes]       = useState<any[]>([]);
  const [stakesLoading,  setStakesLoading]  = useState(false);
  const [activeStake,    setActiveStake]    = useState<any>(null);
  const [splitPct,       setSplitPct]       = useState(50);  // soil %
  const [splitSaving,    setSplitSaving]    = useState(false);
  const [splitMsg,       setSplitMsg]       = useState('');
  const [resending,      setResending]      = useState(false);
  const [resendMsg,      setResendMsg]      = useState('');
  const [showTreeModal,  setShowTreeModal]  = useState(false);
  const [treeInv,        setTreeInv]        = useState<any>(null);
  const [treeInvLoading, setTreeInvLoading] = useState(false);
  const [showPlantModal, setShowPlantModal] = useState(false);
  const [plantSpecies,   setPlantSpecies]   = useState('amaranth');
  const [plantQty,       setPlantQty]       = useState(1);
  const [planting,       setPlanting]       = useState(false);
  const [plantMsg,       setPlantMsg]       = useState('');
  const [annaMinting,    setAnnaMinting]    = useState(false);
  const [annaMsg,        setAnnaMsg]        = useState('');
  const [annaResult,     setAnnaResult]     = useState<{ tx_id: string; asset_id: string; img: string } | null>(null);

  const openNftModal = async () => {
    if (!waxActor) return;
    setShowNftModal(true);
    setStakesLoading(true);
    try {
      const token = localStorage.getItem('token') ?? '';
      const r = await fetch(`/samatvam/api/samatvam/atavi/my-stakes?wax_account=${waxActor}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      setMyStakes(d.stakes ?? []);
      const first = d.stakes?.[0] ?? null;
      setActiveStake(first);
      setSplitPct(first?.soil_pct ?? 50);
      setSplitMsg('');
    } catch { setMyStakes([]); }
    finally { setStakesLoading(false); }
  };

  const openTreeModal = async () => {
    if (!activeStake) return;
    setShowTreeModal(true);
    setTreeInvLoading(true);
    setPlantMsg('');
    try {
      const token = localStorage.getItem('token') ?? '';
      const r = await fetch(`/samatvam/api/samatvam/atavi/nft-trees?wax_account=${waxActor}&asset_id=${activeStake.asset_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      setTreeInv(d);
    } catch { setTreeInv(null); }
    finally { setTreeInvLoading(false); }
  };

  const handlePlant = async () => {
    if (!activeStake || !waxActor || planting) return;
    setPlanting(true);
    setPlantMsg('');
    try {
      const token = localStorage.getItem('token') ?? '';
      const r = await fetch('/samatvam/api/samatvam/atavi/nft-plant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ wax_account: waxActor, asset_id: activeStake.asset_id, species: plantSpecies, quantity: plantQty }),
      });
      const d = await r.json();
      if (d.success) {
        setPlantMsg(`✅ Planted ${d.planted} ${plantSpecies}. Soil remaining: ${d.soil_remaining.toLocaleString()}. Writing to chain…`);
        // Refresh tree inventory and stake data
        await openTreeModal();
        await openNftModal();
      } else {
        setPlantMsg(`❌ ${d.error}`);
      }
    } catch { setPlantMsg('❌ Network error'); }
    finally { setPlanting(false); }
  };

  const handleResend = async () => {
    if (!waxActor || !activeStake || !waxSession || resending) return;
    setResending(true);
    setResendMsg('⏳ Waiting for wallet signature…');
    try {
      const result = await waxSession.transact({
        action: {
          account: 'atomicassets',
          name: 'transfer',
          authorization: [{ actor: waxActor, permission: 'active' }],
          data: {
            from:      waxActor,
            to:        'kalpa.gm',
            asset_ids: [activeStake.asset_id],
            memo:      `atavi:stake:${activeStake.asset_id}`,
          },
        },
      });
      setResendMsg('⏳ Confirming transfer, writing NFT data…');
      // Wait a few seconds for chain to process
      await new Promise(r => setTimeout(r, 6000));
      const token = localStorage.getItem('token') ?? '';
      const r = await fetch('/samatvam/api/samatvam/atavi/stake/fix-nft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ wax_account: waxActor, asset_id: activeStake.asset_id }),
      });
      const d = await r.json();
      if (d.success) {
        setResendMsg('✅ NFT received and data written on-chain.');
        await openNftModal();
      } else {
        setResendMsg(`❌ ${d.error}`);
      }
    } catch (err: any) {
      setResendMsg(`❌ ${err?.message ?? 'Transaction failed'}`);
    } finally {
      setResending(false);
    }
  };

  const saveSplit = async () => {
    if (!waxActor || !activeStake || splitSaving) return;
    setSplitSaving(true);
    setSplitMsg('');
    try {
      const token = localStorage.getItem('token') ?? '';
      const r = await fetch('/samatvam/api/samatvam/atavi/set-split', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ wax_account: waxActor, asset_id: activeStake.asset_id, soil_pct: splitPct }),
      });
      const d = await r.json();
      if (d.success) {
        setSplitMsg(`✅ Split saved — Soil ${d.soil_pct}% / Manure ${d.manure_pct}%. Writing to chain…`);
        // Refresh stake data
        await openNftModal();
      } else {
        setSplitMsg(`❌ ${d.error}`);
      }
    } catch { setSplitMsg('❌ Network error'); }
    finally { setSplitSaving(false); }
  };

  const openStakePanel = async () => {
    if (!waxActor) { alert('Connect your WAX wallet first.'); return; }
    setShowStakePanel(true);
    setSelectedNft(null);
    setNftList([]);
    setNftLoading(true);
    try {
      const r = await fetch(
        `https://wax.api.atomicassets.io/atomicassets/v1/assets?owner=${waxActor}&collection_name=samatvamahvy&schema_name=atavi&limit=50&order=asc&sort=asset_id`
      );
      const d = await r.json();
      setNftList(d.data ?? []);
    } catch { setNftList([]); }
    finally { setNftLoading(false); }
  };

  const loadPreview = async () => {
    if (!selectedNft || !waxActor) return;
    setPreviewLoading(true);
    setStakeMsg('');
    try {
      const token = localStorage.getItem('token') ?? '';
      const r = await fetch(`/samatvam/api/samatvam/atavi/land-count?wax_account=${waxActor}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      setLandCount(d.land_count ?? 0);
      setPlotsToLoad(d.land_count ?? 0);
      setFoodScore(d.food_score ?? 0);
      setMaxPackets(d.max_packets ?? 0);
      setPacketsToLoad(d.max_packets ?? 0);
      setPreviewView(true);
    } catch { setLandCount(0); setPlotsToLoad(0); setFoodScore(0); setMaxPackets(0); setPacketsToLoad(0); setPreviewView(true); }
    finally { setPreviewLoading(false); }
  };

  const handleStake = async () => {
    if (!selectedNft || !waxActor || !waxSession || staking) return;
    setStaking(true);
    setStakeMsg('⏳ Waiting for wallet signature…');
    try {
      // 1. Sign WAX transfer: player → kalpa.gm
      const result = await waxSession.transact({
        action: {
          account: 'atomicassets',
          name: 'transfer',
          authorization: [{ actor: waxActor, permission: 'active' }],
          data: {
            from:     waxActor,
            to:       'kalpa.gm',
            asset_ids: [selectedNft.asset_id],
            memo:     `atavi:stake:${selectedNft.asset_id}`,
          },
        },
      });
      const wax_tx_id = result.response?.transaction_id ?? '';
      setStakeMsg('⏳ Confirming on-chain…');

      // 2. Call backend confirm
      const token = localStorage.getItem('token') ?? '';
      const r = await fetch('/samatvam/api/samatvam/atavi/stake/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          wax_account:    waxActor,
          asset_id:       selectedNft.asset_id,
          plots_to_load:  plotsToLoad,
          food_packets:   packetsToLoad,
          wax_tx_id,
        }),
      });
      const d = await r.json();
      if (d.success) {
        setStakeMsg(`✅ Staked! ${d.plots_transferred.toLocaleString()} plots + ${d.food_packets_loaded} packets loaded.`);
      } else {
        setStakeMsg(`❌ ${d.error}`);
      }
    } catch (err: any) {
      setStakeMsg(`❌ ${err?.message ?? 'Transaction failed'}`);
    } finally {
      setStaking(false);
    }
  };

  const handleHarvestAll = async () => {
    if (!waxActor || harvestBusy) return;
    setHarvestBusy(true); setHarvestMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/plot/harvest-all', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: waxActor }),
      });
      const d = await r.json();
      if (d.success) {
        setHarvestMsg(d.harvested === 0
          ? '⏳ No mature trees yet'
          : `✅ ${d.harvested} trees · +${d.total_logs} logs${d.seeds_returned > 0 ? ` · +${d.seeds_returned} seeds` : ''}`);
      } else {
        setHarvestMsg(`❌ ${d.error}`);
      }
    } catch { setHarvestMsg('❌ Network error'); }
    finally { setHarvestBusy(false); }
  };

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef   = useRef<HTMLDivElement>(null);
  const [pan,  setPan]  = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(0.35);
  const panRef  = useRef({ x: 0, y: 0 });
  const zoomRef = useRef(0.35);
  useEffect(() => { panRef.current  = pan;  }, [pan]);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);

  const drag    = useRef({ active: false, sx: 0, sy: 0, px: 0, py: 0, moved: false });
  const pinch   = useRef({ active: false, dist: 0 });
  const animRef = useRef<number>(0);
  const phaseRef = useRef(0);

  // treeMap: "col,row" → { owner: string[5], rental: string[5] }
  const treeMapRef = useRef<Map<string, { owner: (string|null)[]; rental: (string|null)[] }>>(new Map());

  useEffect(() => {
    fetch('/samatvam/api/samatvam/atavi-forest/all-trees')
      .then(r => r.json())
      .then(d => {
        if (!d.success) return;
        const m = new Map<string, { owner: (string|null)[]; rental: (string|null)[] }>();
        for (const tile of d.trees) {
          const owner  = Array.from({ length: 5 }, (_,i) => SPECIES_EMOJI[(tile.owner_trees  ?? [])[i]?.species] ?? null);
          const rental = Array.from({ length: 5 }, (_,i) => SPECIES_EMOJI[(tile.rental_trees ?? [])[i]?.species] ?? null);
          if (owner.some(Boolean) || rental.some(Boolean)) m.set(`${tile.x},${tile.y}`, { owner, rental });
        }
        treeMapRef.current = m;
      })
      .catch(() => {});
  }, []);

  const [hovered, setHovered] = useState<{ col: number; row: number; ownerIdx: number } | null>(null);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 });

  // centre map on (50,50)
  const isoOffY = -(ROWS * TH / 2);

  const render = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d'); if (!ctx) return;
    const W = canvas.width, H = canvas.height;
    const z = zoomRef.current, p = panRef.current;

    ctx.clearRect(0, 0, W, H);
    ctx.save();
    ctx.translate(W / 2 + p.x, H / 2 + p.y);
    ctx.scale(z, z);
    ctx.translate(0, isoOffY);

    // diagonal render order for correct depth
    for (let d = 0; d < COLS + ROWS - 1; d++) {
      const cStart = Math.max(0, d - ROWS + 1);
      const cEnd   = Math.min(d, COLS - 1);
      for (let col = cStart; col <= cEnd; col++) {
        const row = d - col;
        const isEpicenter = col >= 47 && col <= 51 && row >= 48 && row <= 51;
        if (isEpicenter) {
          const pulse = 0.7 + 0.3 * Math.abs(Math.sin(phaseRef.current * 0.04));
          drawTile(ctx, col, row,
            `rgba(251,191,36,${pulse})`, '#b45309', '#92400e');
        } else {
          const idx = ownerMap.get(`${col},${row}`) ?? -1;
          if (idx < 0) continue;
          const o = OWNERS[idx];
          drawTile(ctx, col, row, o.top, o.s, o.e);
        }

        // Raised platforms + tree emojis
        const tileTrees = treeMapRef.current.get(`${col},${row}`);
        if (tileTrees) {
          const cx = (col - row) * TW / 2;
          const ty = (col + row) * TH / 2 - TILE_H;
          ctx.save();
          ctx.font = '5px serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          // bhoomi: NW edge inset 75% (N→W), rental: SE edge inset 75% (E→S)
          for (let i = 0; i < 5; i++) {
            const t = (i + 0.5) / 5; // 0.1, 0.3, 0.5, 0.7, 0.9
            if (tileTrees.owner[i])
              ctx.fillText(tileTrees.owner[i]!, cx - 12 * t, ty - 6 + 6 * t);
            if (tileTrees.rental[i])
              ctx.fillText(tileTrees.rental[i]!, cx + 12 * (1 - t), ty + 6 * t);
          }
          ctx.restore();
        }
      }
    }

    // coordinate labels at high zoom
    if (z >= 1.5) {
      ctx.font = `${Math.round(4 / z)}px monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      for (let d = 0; d < COLS + ROWS - 1; d++) {
        const cStart = Math.max(0, d - ROWS + 1);
        const cEnd   = Math.min(d, COLS - 1);
        for (let col = cStart; col <= cEnd; col++) {
          const row = d - col;
          const cx = (col - row) * TW / 2;
          const ty = (col + row) * TH / 2 - TILE_H;
          ctx.fillStyle = 'rgba(0,0,0,0.5)';
          ctx.fillText(`${col},${row}`, cx, ty);
        }
      }
    }

    ctx.restore();
  }, [ownerMap, isoOffY]);

  useEffect(() => {
    const loop = () => { phaseRef.current += 1; render(); animRef.current = requestAnimationFrame(loop); };
    animRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(animRef.current);
  }, [render]);

  useEffect(() => {
    const obs = new ResizeObserver(() => {
      const wrap = wrapRef.current, canvas = canvasRef.current;
      if (!wrap || !canvas) return;
      canvas.width  = wrap.clientWidth;
      canvas.height = wrap.clientHeight;
      render();
    });
    if (wrapRef.current) obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, [render]);

  const hitTest = useCallback((mx: number, my: number, cW: number, cH: number) => {
    const z = zoomRef.current, p = panRef.current;
    const wx = (mx - cW / 2 - p.x) / z;
    const wy = (my - cH / 2 - p.y) / z - isoOffY;
    const a = wx / (TW / 2), b = wy / (TH / 2);
    const col = Math.round((a + b) / 2);
    const row = Math.round((b - a) / 2);
    if (col < 0 || col >= COLS || row < 0 || row >= ROWS) return null;
    const idx = ownerMap.get(`${col},${row}`) ?? -1;
    return idx >= 0 ? { col, row, ownerIdx: idx } : null;
  }, [ownerMap, isoOffY]);

  const onMD = useCallback((e: React.MouseEvent) => {
    drag.current = { active: true, sx: e.clientX, sy: e.clientY, px: pan.x, py: pan.y, moved: false };
  }, [pan]);

  const onMM = useCallback((e: React.MouseEvent) => {
    if (drag.current.active) {
      const dx = e.clientX - drag.current.sx, dy = e.clientY - drag.current.sy;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) drag.current.moved = true;
      if (drag.current.moved) setPan({ x: drag.current.px + dx, y: drag.current.py + dy });
    }
    const canvas = canvasRef.current; if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    setHovered(hitTest(e.clientX - rect.left, e.clientY - rect.top, rect.width, rect.height));
    setHoverPos({ x: e.clientX, y: e.clientY });
  }, [hitTest]);

  const onMU = useCallback(() => { drag.current.active = false; }, []);

  const onWh = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const canvas = canvasRef.current; if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left - rect.width / 2;
    const my = e.clientY - rect.top  - rect.height / 2;
    const oldZ = zoomRef.current;
    const newZ = Math.max(0.1, Math.min(8, oldZ - e.deltaY * 0.001));
    const sc = newZ / oldZ, p = panRef.current;
    setPan({ x: mx - (mx - p.x) * sc, y: my - (my - p.y) * sc });
    setZoom(newZ);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const h = (e: TouchEvent) => { if (e.touches.length > 1) e.preventDefault(); };
    canvas.addEventListener('touchmove', h, { passive: false });
    return () => canvas.removeEventListener('touchmove', h);
  }, []);

  const onTE = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      const t = e.touches[0];
      drag.current = { active: true, sx: t.clientX, sy: t.clientY, px: pan.x, py: pan.y, moved: false };
      pinch.current.active = false;
    } else if (e.touches.length === 2) {
      const t1 = e.touches[0], t2 = e.touches[1];
      const dx = t2.clientX - t1.clientX, dy = t2.clientY - t1.clientY;
      pinch.current = { active: true, dist: Math.sqrt(dx * dx + dy * dy) };
      drag.current.active = false;
    }
  }, [pan]);

  const onTM = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && pinch.current.active) {
      const t1 = e.touches[0], t2 = e.touches[1];
      const dx = t2.clientX - t1.clientX, dy = t2.clientY - t1.clientY;
      const newDist = Math.sqrt(dx * dx + dy * dy);
      const canvas = canvasRef.current; if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const cx = (t1.clientX + t2.clientX) / 2 - rect.left - rect.width / 2;
      const cy = (t1.clientY + t2.clientY) / 2 - rect.top  - rect.height / 2;
      const oldZ = zoomRef.current;
      const newZ = Math.max(0.1, Math.min(8, oldZ * (newDist / pinch.current.dist)));
      const sc = newZ / oldZ, p = panRef.current;
      setPan({ x: cx - (cx - p.x) * sc, y: cy - (cy - p.y) * sc });
      setZoom(newZ);
      pinch.current.dist = newDist;
    } else if (e.touches.length === 1 && drag.current.active) {
      const t = e.touches[0];
      const dx = t.clientX - drag.current.sx, dy = t.clientY - drag.current.sy;
      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) drag.current.moved = true;
      if (drag.current.moved) setPan({ x: drag.current.px + dx, y: drag.current.py + dy });
    }
  }, []);

  const onTEnd = useCallback(() => {
    drag.current.active = false;
    pinch.current.active = false;
  }, []);

  return (
    <div style={{ width: '100%', height: '100vh', background: '#060c06', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: 'monospace' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', background: '#0a120a', borderBottom: '1px solid #1a2a1a', flexShrink: 0 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <ArrowLeft size={16} /> Back
        </button>
        <span style={{ color: '#fbbf24', fontWeight: 'bold', fontSize: 15, letterSpacing: 1 }}>⬡ ATAVI FOREST — PLOT MAP</span>
        <span style={{ color: '#374151', fontSize: 11, marginLeft: 4 }}>— 10,000 plots · 24 owners · zoom in for coordinates</span>

        {!waxActor && <div style={{ marginLeft: 'auto' }} />}
        {waxActor && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 10, color: '#6b7280', fontFamily: 'monospace' }}>⬡ {waxActor}</span>
            <button onClick={handleHarvestAll} disabled={harvestBusy || ATAVI_LOCKED}
              style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: (harvestBusy || ATAVI_LOCKED) ? 'default' : 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap',
                background: 'rgba(52,211,153,0.04)',
                border: '1px solid rgba(52,211,153,0.1)',
                color: 'rgba(52,211,153,0.3)' }}>
              {harvestBusy ? '⏳ Harvesting…' : '🔒 Harvest All'}
            </button>
            {harvestMsg && <span style={{ fontSize: 11, fontWeight: 700, color: harvestMsg.startsWith('✅') ? '#6ee7b7' : harvestMsg.startsWith('⏳') ? '#fbbf24' : '#f87171' }}>{harvestMsg}</span>}
          </div>
        )}
      </div>

      {/* Reset notice */}
      <div style={{ background: 'linear-gradient(90deg, rgba(245,158,11,0.1), rgba(245,158,11,0.05))', borderBottom: '1px solid rgba(245,158,11,0.2)', padding: '10px 16px', display: 'flex', alignItems: 'flex-start', gap: 10, flexShrink: 0 }}>
        <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>🌿</span>
        <div>
          <span style={{ fontWeight: 800, color: '#fbbf24', fontSize: 12 }}>Scheduled Reset — 19 May 2026, 12:00 GMT &nbsp;</span>
          <span style={{ color: '#64748b', fontSize: 12 }}>
            All trees will be harvested automatically. Seeds and Bhoomi tokens will be returned in full. Planting and harvesting paused for ~10–15 min. All other game sections run as usual.
          </span>
        </div>
      </div>

      {/* Control bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '10px 16px', background: '#080f08', borderBottom: '1px solid #1f2d1f', flexShrink: 0 }}>
        <button onClick={onOpenAtaviAssets}
          style={{ padding: '11px 32px', borderRadius: 10, fontSize: 15, fontWeight: 900, letterSpacing: 0.5, cursor: 'pointer', background: 'linear-gradient(135deg, rgba(251,191,36,0.18), rgba(245,158,11,0.12))', border: '1.5px solid rgba(251,191,36,0.6)', color: '#fbbf24', boxShadow: '0 0 20px rgba(251,191,36,0.2)', transition: 'all 0.2s' }}
          onMouseEnter={e => { const b = e.currentTarget as HTMLButtonElement; b.style.background = 'linear-gradient(135deg, rgba(251,191,36,0.28), rgba(245,158,11,0.22))'; b.style.boxShadow = '0 0 32px rgba(251,191,36,0.4)'; b.style.color = '#fde68a'; }}
          onMouseLeave={e => { const b = e.currentTarget as HTMLButtonElement; b.style.background = 'linear-gradient(135deg, rgba(251,191,36,0.18), rgba(245,158,11,0.12))'; b.style.boxShadow = '0 0 20px rgba(251,191,36,0.2)'; b.style.color = '#fbbf24'; }}
        >⬡ Control your Atavi Forest →</button>

        {waxActor && (
          <button onClick={openNftModal}
            style={{ padding: '11px 32px', borderRadius: 10, fontSize: 15, fontWeight: 900, letterSpacing: 0.5, cursor: 'pointer', background: 'linear-gradient(135deg, rgba(139,92,246,0.18), rgba(109,40,217,0.12))', border: '1.5px solid rgba(139,92,246,0.6)', color: '#c4b5fd', boxShadow: '0 0 20px rgba(139,92,246,0.2)', transition: 'all 0.2s' }}
            onMouseEnter={e => { const b = e.currentTarget as HTMLButtonElement; b.style.background = 'linear-gradient(135deg, rgba(139,92,246,0.28), rgba(109,40,217,0.22))'; b.style.color = '#ddd6fe'; }}
            onMouseLeave={e => { const b = e.currentTarget as HTMLButtonElement; b.style.background = 'linear-gradient(135deg, rgba(139,92,246,0.18), rgba(109,40,217,0.12))'; b.style.color = '#c4b5fd'; }}
          >⬡ My Staked NFTs</button>
        )}

        <button onClick={openStakePanel}
          style={{ padding: '11px 32px', borderRadius: 10, fontSize: 15, fontWeight: 900, letterSpacing: 0.5, cursor: 'pointer', background: 'linear-gradient(135deg, rgba(110,231,183,0.18), rgba(52,211,153,0.12))', border: '1.5px solid rgba(52,211,153,0.6)', color: '#6ee7b7', boxShadow: '0 0 20px rgba(52,211,153,0.2)', transition: 'all 0.2s' }}
          onMouseEnter={e => { const b = e.currentTarget as HTMLButtonElement; b.style.background = 'linear-gradient(135deg, rgba(110,231,183,0.28), rgba(52,211,153,0.22))'; b.style.boxShadow = '0 0 32px rgba(52,211,153,0.4)'; b.style.color = '#a7f3d0'; }}
          onMouseLeave={e => { const b = e.currentTarget as HTMLButtonElement; b.style.background = 'linear-gradient(135deg, rgba(110,231,183,0.18), rgba(52,211,153,0.12))'; b.style.boxShadow = '0 0 20px rgba(52,211,153,0.2)'; b.style.color = '#6ee7b7'; }}
        >⬡ Stake your Atavi NFT</button>
      </div>

      {/* Canvas */}
      <div ref={wrapRef} style={{ flex: 1, position: 'relative', overflow: 'hidden', cursor: 'grab' }}>
        <canvas
          ref={canvasRef}
          style={{ display: 'block', width: '100%', height: '100%' }}
          onMouseDown={onMD} onMouseMove={onMM} onMouseUp={onMU} onMouseLeave={onMU}
          onWheel={onWh} onTouchStart={onTE} onTouchMove={onTM} onTouchEnd={onTEnd}
        />

        {hovered && (
          <div style={{
            position: 'fixed', left: hoverPos.x + 14, top: hoverPos.y - 10,
            background: 'rgba(0,0,0,0.9)', border: `1px solid ${OWNERS[hovered.ownerIdx].top}`,
            borderRadius: 6, padding: '4px 10px', fontSize: 11, color: OWNERS[hovered.ownerIdx].top,
            pointerEvents: 'none', zIndex: 100, whiteSpace: 'nowrap',
          }}>
            {OWNERS[hovered.ownerIdx].name} · ({hovered.col},{hovered.row})
          </div>
        )}

        <div style={{ position: 'absolute', bottom: 16, right: 16, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {([['＋', 0.15], ['－', -0.15]] as [string, number][]).map(([label, delta]) => (
            <button key={label}
              onClick={() => setZoom(z => Math.max(0.1, Math.min(8, z + delta)))}
              style={{ width: 32, height: 32, background: 'rgba(0,0,0,0.6)', border: '1px solid #1f2937', borderRadius: 6, color: '#9ca3af', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >{label}</button>
          ))}
          <button onClick={() => { setPan({ x: 0, y: 0 }); setZoom(0.35); }}
            style={{ width: 32, height: 32, background: 'rgba(0,0,0,0.6)', border: '1px solid #1f2937', borderRadius: 6, color: '#6b7280', cursor: 'pointer', fontSize: 10 }}
          >⌂</button>
        </div>

        <div style={{ position: 'absolute', bottom: 16, left: 16, fontSize: 11, color: '#374151' }}>
          (0,0) → (99,99) · 10,000 plots
        </div>
      </div>

      {/* ── Staked NFT modal — full screen width ── */}
      {showNftModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 200, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 32px', background: '#0a0a18', borderBottom: '1px solid rgba(139,92,246,0.2)', flexShrink: 0 }}>
            <div style={{ color: '#c4b5fd', fontWeight: 900, fontSize: 20, letterSpacing: 0.5 }}>⬡ My Staked Atavi NFTs</div>
            {myStakes.length > 1 && (
              <div style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
                {myStakes.map(s => (
                  <button key={s.asset_id} onClick={() => { setActiveStake(s); setSplitPct(s.soil_pct ?? 50); setSplitMsg(''); }}
                    style={{ padding: '7px 20px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', background: activeStake?.asset_id === s.asset_id ? 'rgba(139,92,246,0.2)' : 'none', border: `1px solid ${activeStake?.asset_id === s.asset_id ? 'rgba(139,92,246,0.6)' : '#374151'}`, color: activeStake?.asset_id === s.asset_id ? '#c4b5fd' : '#6b7280' }}
                  >Atavi · {s.asset_id.slice(-6)}</button>
                ))}
              </div>
            )}
            <button onClick={() => setShowNftModal(false)}
              style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 26, lineHeight: 1 }}>✕</button>
          </div>

          {/* Body */}
          <div style={{ overflowY: 'auto', flex: 1, padding: '28px 40px' }}>
            {stakesLoading && <div style={{ color: '#4b5563', fontSize: 16, textAlign: 'center', paddingTop: 60 }}>Loading…</div>}
            {!stakesLoading && myStakes.length === 0 && (
              <div style={{ color: '#4b5563', fontSize: 16, textAlign: 'center', paddingTop: 60 }}>
                No staked Atavi NFTs found.<br />
                <span style={{ fontSize: 13, color: '#374151', marginTop: 8, display: 'block' }}>Use "Stake your Atavi NFT" to stake one.</span>
              </div>
            )}
            {!stakesLoading && activeStake && (() => {
              const md = activeStake.mutable_data ?? {};
              const landCount = activeStake.land_count ?? 0;
              const totalRate = (landCount * 0.3).toFixed(1);
              const soilRate  = (landCount * 0.3 * (splitPct / 100)).toFixed(1);
              const manureRate = (landCount * 0.3 * ((100 - splitPct) / 100)).toFixed(1);
              return (
                <div style={{ maxWidth: 1400, margin: '0 auto' }}>
                  {/* ── NFT not at custodian warning ── */}
                  {!activeStake.nft_at_custodian && (
                    <div style={{ marginBottom: 24, padding: '20px 24px', borderRadius: 12, background: 'rgba(245,158,11,0.06)', border: '1.5px solid rgba(245,158,11,0.4)', boxShadow: '0 0 24px rgba(245,158,11,0.08)' }}>
                      <div style={{ color: '#fbbf24', fontWeight: 900, fontSize: 17, marginBottom: 8 }}>⚠ NFT not received yet</div>
                      <div style={{ color: '#9ca3af', fontSize: 15, lineHeight: 1.7, marginBottom: 16 }}>
                        Your staking transaction did not go through. Your NFT is still in your wallet.<br />
                        Click below to send it to the staking pool — your land and resources are already reserved.
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                        <button onClick={handleResend} disabled={resending}
                          style={{ padding: '12px 32px', borderRadius: 10, fontSize: 15, fontWeight: 900, cursor: resending ? 'default' : 'pointer', background: 'linear-gradient(135deg, rgba(245,158,11,0.25), rgba(217,119,6,0.2))', border: '1.5px solid rgba(245,158,11,0.6)', color: '#fbbf24', opacity: resending ? 0.6 : 1 }}>
                          {resending ? '⏳ Sending…' : '⬡ Send NFT to Stake →'}
                        </button>
                        {resendMsg && <span style={{ fontSize: 14, fontWeight: 700, color: resendMsg.startsWith('✅') ? '#6ee7b7' : resendMsg.startsWith('❌') ? '#f87171' : '#fbbf24' }}>{resendMsg}</span>}
                      </div>
                    </div>
                  )}

                  {/* NFT image + title */}
                  <div style={{ display: 'flex', gap: 28, marginBottom: 28, alignItems: 'flex-start' }}>
                    <img src={md.img || 'https://gyn.ahantravyuh.com/images/atavi-base.png'} alt="Atavi NFT"
                      style={{ width: 120, height: 120, borderRadius: 12, border: '1.5px solid rgba(139,92,246,0.4)', objectFit: 'cover', flexShrink: 0 }} />
                    <div>
                      <div style={{ color: '#c4b5fd', fontWeight: 900, fontSize: 26 }}>Atavi NFT</div>
                      <div style={{ color: '#6b7280', fontSize: 14, marginTop: 2, fontFamily: 'monospace' }}>{activeStake.asset_id}</div>
                      <div style={{ color: '#9ca3af', fontSize: 14, marginTop: 8, lineHeight: 1.5 }}>{md.description}</div>
                      <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
                        <span style={{ fontSize: 13, background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.3)', borderRadius: 6, padding: '4px 12px', color: '#c4b5fd' }}>📍 {md.location || 'Glasgow'}</span>
                        <span style={{ fontSize: 13, background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.2)', borderRadius: 6, padding: '4px 12px', color: '#6ee7b7' }}>Level {md.level ?? 0}</span>
                      </div>
                    </div>
                  </div>

                  {/* Land & Trees row */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 24 }}>
                    <div style={{ background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.2)', borderRadius: 12, padding: '20px 24px' }}>
                      <div style={{ color: '#9ca3af', fontSize: 12, marginBottom: 6, letterSpacing: 1 }}>ATAVI GLASGOW LAND</div>
                      <div style={{ color: '#6ee7b7', fontWeight: 900, fontSize: 36 }}>{landCount.toLocaleString()}</div>
                      <div style={{ color: '#374151', fontSize: 14, marginTop: 2 }}>plots loaded</div>
                    </div>
                    <div onClick={activeStake.nft_at_custodian ? openTreeModal : undefined}
                      style={{ background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: 12, padding: '20px 24px', cursor: activeStake.nft_at_custodian ? 'pointer' : 'not-allowed', transition: 'all 0.15s', opacity: activeStake.nft_at_custodian ? 1 : 0.35 }}
                      onMouseEnter={e => { if (!activeStake.nft_at_custodian) return; (e.currentTarget as HTMLDivElement).style.background = 'rgba(139,92,246,0.12)'; (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(139,92,246,0.5)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'rgba(139,92,246,0.06)'; (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(139,92,246,0.2)'; }}>
                      <div style={{ color: '#9ca3af', fontSize: 12, marginBottom: 6, letterSpacing: 1 }}>TREES {activeStake.nft_at_custodian && <span style={{ color: '#6b7280', fontSize: 11 }}>— click for inventory</span>}</div>
                      <div style={{ color: '#c4b5fd', fontWeight: 900, fontSize: 36 }}>{(activeStake.total_trees ?? 0).toLocaleString()}</div>
                      <div style={{ color: '#374151', fontSize: 14, marginTop: 2 }}>{activeStake.matured_trees ?? 0} matured · {(activeStake.total_trees ?? 0) - (activeStake.matured_trees ?? 0)} growing</div>
                    </div>
                  </div>

                  {/* ── Gate: everything below disabled until NFT confirmed ── */}
                  <div style={{ position: 'relative', opacity: activeStake.nft_at_custodian ? 1 : 0.25, pointerEvents: activeStake.nft_at_custodian ? 'auto' : 'none' }}>

                  {/* Soil / Manure split setter */}
                  <div style={{ background: 'rgba(134,239,172,0.04)', border: '1.5px solid rgba(134,239,172,0.2)', borderRadius: 12, padding: '22px 28px', marginBottom: 24 }}>
                    <div style={{ color: '#9ca3af', fontSize: 12, letterSpacing: 1, marginBottom: 14 }}>SOIL / MANURE SPLIT</div>
                    <div style={{ color: '#d1fae5', fontSize: 15, marginBottom: 16 }}>
                      Total accrual: <strong style={{ color: '#6ee7b7', fontSize: 17 }}>{totalRate}/hr</strong> from {landCount.toLocaleString()} plots.
                      &nbsp;Set how it splits between Soil and Manure.
                    </div>

                    {/* Slider */}
                    <div style={{ marginBottom: 14 }}>
                      <input type="range" min={0} max={100} value={splitPct}
                        onChange={e => setSplitPct(Number(e.target.value))}
                        style={{ width: '100%', accentColor: '#86efac', cursor: 'pointer', height: 6 }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
                        <div style={{ textAlign: 'left' }}>
                          <div style={{ color: '#86efac', fontWeight: 900, fontSize: 22 }}>{splitPct}%</div>
                          <div style={{ color: '#9ca3af', fontSize: 13 }}>Soil — {soilRate}/hr</div>
                          <div style={{ color: '#6b7280', fontSize: 13 }}>current: {(md.p1 ?? 0).toLocaleString()}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ color: '#fde68a', fontWeight: 900, fontSize: 22 }}>{100 - splitPct}%</div>
                          <div style={{ color: '#9ca3af', fontSize: 13 }}>Manure — {manureRate}/hr</div>
                          <div style={{ color: '#6b7280', fontSize: 13 }}>current: {(md.p2 ?? 0).toLocaleString()}</div>
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 6 }}>
                      <button onClick={saveSplit} disabled={splitSaving}
                        style={{ padding: '10px 32px', borderRadius: 9, fontSize: 15, fontWeight: 800, cursor: splitSaving ? 'default' : 'pointer', background: 'linear-gradient(135deg, rgba(134,239,172,0.2), rgba(52,211,153,0.15))', border: '1.5px solid rgba(134,239,172,0.5)', color: '#86efac', opacity: splitSaving ? 0.6 : 1 }}>
                        {splitSaving ? '⏳ Saving…' : 'Save Split →'}
                      </button>
                      {splitMsg && <span style={{ fontSize: 14, fontWeight: 700, color: splitMsg.startsWith('✅') ? '#86efac' : '#f87171' }}>{splitMsg}</span>}
                    </div>
                  </div>

                  {/* ON-CHAIN RESOURCES */}
                  <div style={{ color: '#9ca3af', fontSize: 12, marginBottom: 12, letterSpacing: 1 }}>ON-CHAIN RESOURCES</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 28 }}>
                    {[
                      { key: 'p1',  label: 'Soil',          color: '#86efac' },
                      { key: 'p2',  label: 'Manure',        color: '#fde68a' },
                      { key: 'p3',  label: 'Food Packets',  color: '#fbbf24' },
                      { key: 'p4',  label: 'P4',            color: '#4b5563' },
                      { key: 'p5',  label: 'P5',            color: '#4b5563' },
                      { key: 'p6',  label: 'P6',            color: '#4b5563' },
                      { key: 'p7',  label: 'P7',            color: '#4b5563' },
                      { key: 'p8',  label: 'P8',            color: '#4b5563' },
                      { key: 'p9',  label: 'Plot Count',    color: '#7dd3fc' },
                      { key: 'p10', label: 'P10',           color: '#4b5563' },
                    ].map(p => (
                      <div key={p.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '16px 10px', borderRadius: 10, background: 'rgba(255,255,255,0.025)', border: `1px solid ${p.color === '#4b5563' ? 'rgba(75,85,99,0.2)' : 'rgba(139,92,246,0.12)'}`, gap: 6 }}>
                        <span style={{ fontSize: 12, color: p.color, fontWeight: 700, textAlign: 'center' }}>{p.label}</span>
                        <span style={{ fontSize: 20, color: p.color, fontFamily: 'monospace', fontWeight: 900 }}>{(md[p.key] ?? 0).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>

                  {/* Mint buttons */}
                  <div style={{ color: '#9ca3af', fontSize: 12, marginBottom: 12, letterSpacing: 1 }}>MINT FROM THIS NFT</div>
                  <div style={{ display: 'flex', gap: 14, flexDirection: 'column' }}>

                    {/* Anna NFT Mint */}
                    <div style={{ background: 'rgba(251,191,36,0.04)', border: '1.5px solid rgba(251,191,36,0.2)', borderRadius: 14, padding: '20px 24px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                        <div>
                          <div style={{ color: '#fbbf24', fontWeight: 900, fontSize: 16 }}>🌾 Mint Anna NFT</div>
                          <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 3 }}>100,000 food packets → 1 Anna NFT (Level 1 food product)</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ color: '#fbbf24', fontWeight: 900, fontSize: 18 }}>{(md.p3 ?? 0).toLocaleString()}</div>
                          <div style={{ color: '#9ca3af', fontSize: 11 }}>food packets on NFT</div>
                        </div>
                      </div>

                      {(md.p3 ?? 0) >= 100000 ? (
                        <button
                          onClick={async () => {
                            if (annaMinting || !waxActor) return;
                            setAnnaMinting(true); setAnnaMsg(''); setAnnaResult(null);
                            try {
                              const token = localStorage.getItem('token') ?? '';
                              const r = await fetch('/samatvam/api/samatvam/anna/mint', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                body: JSON.stringify({ wax_account: waxActor }),
                              });
                              const d = await r.json();
                              if (d.success) {
                                setAnnaResult({ tx_id: d.tx_id, asset_id: d.asset_id, img: d.img });
                                setAnnaMsg('✅ ' + d.message);
                              } else {
                                setAnnaMsg('❌ ' + (d.error ?? 'Mint failed'));
                              }
                            } catch { setAnnaMsg('❌ Network error'); }
                            setAnnaMinting(false);
                          }}
                          disabled={annaMinting}
                          style={{ width: '100%', padding: '13px', borderRadius: 10, fontSize: 15, fontWeight: 800, cursor: annaMinting ? 'default' : 'pointer', background: annaMinting ? 'rgba(251,191,36,0.05)' : 'linear-gradient(135deg, rgba(251,191,36,0.25), rgba(245,158,11,0.2))', border: '1.5px solid rgba(251,191,36,0.5)', color: '#fbbf24', opacity: annaMinting ? 0.6 : 1 }}
                        >
                          {annaMinting ? '⏳ Minting…' : '🌾 Mint Anna NFT · 100,000 food packets'}
                        </button>
                      ) : (
                        <div style={{ padding: '12px', borderRadius: 10, background: 'rgba(75,85,99,0.1)', border: '1px solid rgba(75,85,99,0.2)', color: '#6b7280', fontSize: 13, textAlign: 'center' }}>
                          Need {(100000 - (md.p3 ?? 0)).toLocaleString()} more food packets to mint
                        </div>
                      )}

                      {annaMsg && (
                        <div style={{ marginTop: 12, fontSize: 13, fontWeight: 700, color: annaMsg.startsWith('✅') ? '#86efac' : '#f87171', lineHeight: 1.5 }}>{annaMsg}</div>
                      )}

                      {annaResult && (
                        <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 16, background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 10, padding: '14px 18px' }}>
                          <img src={annaResult.img} alt="Anna NFT" style={{ width: 64, height: 64, borderRadius: 8, objectFit: 'cover', border: '2px solid rgba(251,191,36,0.4)' }} />
                          <div>
                            <div style={{ color: '#fbbf24', fontWeight: 900, fontSize: 14 }}>Anna NFT Minted!</div>
                            <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 4 }}>Asset: {annaResult.asset_id}</div>
                            <a href={`https://wax.atomichub.io/explorer/asset/wax-mainnet/${annaResult.asset_id}`} target="_blank" rel="noreferrer"
                              style={{ color: '#60a5fa', fontSize: 12, textDecoration: 'underline', display: 'inline-block', marginTop: 4 }}>
                              View on AtomicHub →
                            </a>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Coming soon buttons */}
                    <div style={{ display: 'flex', gap: 14 }}>
                      <button style={{ flex: 1, padding: '16px', borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: 'not-allowed', background: 'rgba(139,92,246,0.05)', border: '1px solid rgba(139,92,246,0.15)', color: '#6b7280' }}>
                        🌿 Mint Atavi Land NFTs<br /><span style={{ fontSize: 12, fontWeight: 400 }}>coming soon</span>
                      </button>
                      <button style={{ flex: 1, padding: '16px', borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: 'not-allowed', background: 'rgba(139,92,246,0.05)', border: '1px solid rgba(139,92,246,0.15)', color: '#6b7280' }}>
                        🌲 Mint Tree NFTs<br /><span style={{ fontSize: 12, fontWeight: 400 }}>coming soon</span>
                      </button>
                    </div>
                  </div>

                  </div>{/* end gate wrapper */}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* ── Tree Inventory Modal ── */}
      {showTreeModal && activeStake && (() => {
        const GYN = 'https://gyn.ahantravyuh.com/images';
        const SPECIES_INFO: Record<string, { label: string; maturity: string; cost: number; img: string; glow: string; accent: string }> = {
          amaranth: { label: 'Amaranth', maturity: '4h',   cost: 10,  img: `${GYN}/atavi-amaranth.png`, glow: '34,197,94',   accent: '#86efac' },
          vetiver:  { label: 'Vetiver',  maturity: '8h',   cost: 20,  img: `${GYN}/atavi-vetiver.png`,  glow: '163,230,53',  accent: '#d9f99d' },
          opuntia:  { label: 'Opuntia',  maturity: '16h',  cost: 35,  img: `${GYN}/atavi-opuntia.png`,  glow: '250,204,21',  accent: '#fde047' },
          moringa:  { label: 'Moringa',  maturity: '24h',  cost: 50,  img: `${GYN}/atavi-moringa.png`,  glow: '52,211,153',  accent: '#6ee7b7' },
          jujube:   { label: 'Jujube',   maturity: '36h',  cost: 70,  img: `${GYN}/atavi-jujube.png`,   glow: '251,146,60',  accent: '#fdba74' },
          acacia:   { label: 'Acacia',   maturity: '48h',  cost: 90,  img: `${GYN}/atavi-acacia.png`,   glow: '167,139,250', accent: '#c4b5fd' },
          mesquite: { label: 'Mesquite', maturity: '72h',  cost: 120, img: `${GYN}/atavi-mesquite.png`, glow: '96,165,250',  accent: '#93c5fd' },
          neem:     { label: 'Neem',     maturity: '120h', cost: 160, img: `${GYN}/atavi-neem.png`,     glow: '240,171,252', accent: '#f5d0fe' },
        };
        const md = activeStake.mutable_data ?? {};
        const liveP1 = md.p1 ?? 0;
        const trees  = treeInv?.trees ?? {};
        const availableSlots = treeInv?.available_slots ?? 0;
        const SOIL_COST: Record<string, number> = { amaranth:10, vetiver:20, opuntia:35, moringa:50, jujube:70, acacia:90, mesquite:120, neem:160 };
        const maxCanPlant = Math.floor(liveP1 / (SOIL_COST[plantSpecies] ?? 10));
        const totalTrees = Object.values(trees).reduce((s: number, v: any) => s + (v.total ?? 0), 0);
        const soilPct = activeStake.soil_pct ?? 50;

        return (
          <div style={{ position: 'fixed', inset: 0, background: '#030a05', zIndex: 300, display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: 'monospace' }}>

            {/* ── Full-screen header ── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 36px', background: 'linear-gradient(90deg, rgba(6,20,8,0.95), rgba(4,15,6,0.95))', borderBottom: '1px solid rgba(52,211,153,0.15)', flexShrink: 0 }}>
              <div>
                <div style={{ color: '#6ee7b7', fontWeight: 900, fontSize: 22, letterSpacing: 1 }}>
                  🌲 ATAVI FOREST — TREE INVENTORY
                </div>
                <div style={{ color: '#4b5563', fontSize: 13, marginTop: 3 }}>
                  NFT {activeStake.asset_id} &nbsp;·&nbsp; {activeStake.land_count} plots &nbsp;·&nbsp; {availableSlots.toLocaleString()} planting slots open
                </div>
              </div>

              {/* Resource pills */}
              <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                <div style={{ textAlign: 'center', background: 'rgba(134,239,172,0.06)', border: '1px solid rgba(134,239,172,0.2)', borderRadius: 10, padding: '10px 20px' }}>
                  <div style={{ color: '#6b7280', fontSize: 11, letterSpacing: 1 }}>SOIL (p1)</div>
                  <div style={{ color: '#86efac', fontWeight: 900, fontSize: 24 }}>{liveP1.toLocaleString()}</div>
                  <div style={{ color: '#374151', fontSize: 11 }}>{(activeStake.land_count * 0.3 * (soilPct / 100)).toFixed(1)}/hr</div>
                </div>
                <div style={{ textAlign: 'center', background: 'rgba(253,224,71,0.06)', border: '1px solid rgba(253,224,71,0.15)', borderRadius: 10, padding: '10px 20px' }}>
                  <div style={{ color: '#6b7280', fontSize: 11, letterSpacing: 1 }}>MANURE (p2)</div>
                  <div style={{ color: '#fde68a', fontWeight: 900, fontSize: 24 }}>{(md.p2 ?? 0).toLocaleString()}</div>
                  <div style={{ color: '#374151', fontSize: 11 }}>{(activeStake.land_count * 0.3 * ((100 - soilPct) / 100)).toFixed(1)}/hr</div>
                </div>
                <div style={{ textAlign: 'center', background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.15)', borderRadius: 10, padding: '10px 20px' }}>
                  <div style={{ color: '#6b7280', fontSize: 11, letterSpacing: 1 }}>TOTAL TREES</div>
                  <div style={{ color: '#c4b5fd', fontWeight: 900, fontSize: 24 }}>{totalTrees.toLocaleString()}</div>
                  <div style={{ color: '#374151', fontSize: 11 }}>{activeStake.matured_trees ?? 0} matured</div>
                </div>

                <button onClick={() => setShowPlantModal(true)}
                  style={{ padding: '12px 32px', borderRadius: 10, fontSize: 16, fontWeight: 900, cursor: 'pointer', background: 'linear-gradient(135deg, rgba(52,211,153,0.25), rgba(16,185,129,0.2))', border: '1.5px solid rgba(52,211,153,0.6)', color: '#6ee7b7', boxShadow: '0 0 24px rgba(52,211,153,0.2)', letterSpacing: 0.5 }}>
                  🌱 Plant Trees
                </button>
                <button onClick={() => { setShowTreeModal(false); setShowPlantModal(false); }}
                  style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 28, lineHeight: 1 }}>✕</button>
              </div>
            </div>

            {/* ── Species cards grid ── */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '28px 36px' }}>
              {treeInvLoading && <div style={{ color: '#4b5563', fontSize: 18, textAlign: 'center', paddingTop: 80 }}>Loading…</div>}
              {!treeInvLoading && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 18 }}>
                  {Object.entries(SPECIES_INFO).map(([sp, info]) => {
                    const inv = trees[sp] ?? { total: 0, matured: 0, growing: 0 };
                    const hasAny = inv.total > 0;
                    const pct = inv.total > 0 ? Math.round((inv.matured / inv.total) * 100) : 0;
                    return (
                      <div key={sp} style={{
                        borderRadius: 16,
                        border: `1.5px solid rgba(${info.glow}, ${hasAny ? '0.35' : '0.08'})`,
                        background: hasAny
                          ? `linear-gradient(160deg, rgba(${info.glow},0.1) 0%, rgba(3,10,5,0.95) 60%)`
                          : 'rgba(10,18,10,0.6)',
                        boxShadow: hasAny ? `0 0 32px rgba(${info.glow},0.12)` : 'none',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.2s',
                        opacity: hasAny ? 1 : 0.45,
                      }}>
                        {/* Species image */}
                        <div style={{ position: 'relative', height: 160, overflow: 'hidden', background: `radial-gradient(ellipse at center, rgba(${info.glow},0.15) 0%, transparent 70%)` }}>
                          <img src={info.img} alt={info.label}
                            style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '12px', filter: hasAny ? 'none' : 'grayscale(80%) brightness(0.5)' }} />
                          {hasAny && (
                            <div style={{ position: 'absolute', top: 10, right: 10, background: `rgba(${info.glow},0.2)`, border: `1px solid rgba(${info.glow},0.5)`, borderRadius: 8, padding: '3px 10px', color: info.accent, fontSize: 13, fontWeight: 900 }}>
                              ×{inv.total}
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div style={{ padding: '14px 18px', flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                            <span style={{ color: info.accent, fontWeight: 900, fontSize: 18 }}>{info.label}</span>
                            <span style={{ color: '#4b5563', fontSize: 12 }}>matures {info.maturity}</span>
                          </div>
                          <div style={{ color: '#374151', fontSize: 12 }}>Soil cost: {info.cost}/tree</div>

                          {hasAny && (
                            <>
                              {/* Maturity bar */}
                              <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 6, height: 6, overflow: 'hidden', marginTop: 4 }}>
                                <div style={{ height: '100%', width: `${pct}%`, background: `rgba(${info.glow},0.8)`, borderRadius: 6, transition: 'width 0.4s' }} />
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                                <span style={{ color: info.accent, fontWeight: 700 }}>✓ {inv.matured} matured</span>
                                <span style={{ color: '#fbbf24', fontWeight: 700 }}>⏳ {inv.growing} growing</span>
                              </div>
                            </>
                          )}
                          {!hasAny && (
                            <div style={{ color: '#374151', fontSize: 13, marginTop: 4 }}>No trees yet</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── Plant Trees overlay ── */}
            {showPlantModal && (
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ background: '#050f06', border: '1.5px solid rgba(52,211,153,0.4)', borderRadius: 16, width: '94%', maxWidth: 780, overflow: 'hidden', boxShadow: '0 0 60px rgba(52,211,153,0.12)' }}>

                  {/* Plant header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 28px', borderBottom: '1px solid rgba(52,211,153,0.12)', background: 'rgba(52,211,153,0.04)' }}>
                    <div>
                      <div style={{ color: '#6ee7b7', fontWeight: 900, fontSize: 20 }}>🌱 Plant Trees on your Atavi Forest</div>
                      <div style={{ color: '#4b5563', fontSize: 13, marginTop: 4 }}>
                        Soil available: <span style={{ color: '#86efac', fontWeight: 900, fontSize: 16 }}>{liveP1.toLocaleString()}</span>
                        &nbsp;·&nbsp; Slots open: <span style={{ color: '#c4b5fd', fontWeight: 700 }}>{availableSlots}</span>
                      </div>
                    </div>
                    <button onClick={() => { setShowPlantModal(false); setPlantMsg(''); }}
                      style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 26 }}>✕</button>
                  </div>

                  {/* Species cards — 4 per row */}
                  <div style={{ padding: '20px 28px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                    {Object.entries(SPECIES_INFO).map(([sp, info]) => {
                      const isSelected = plantSpecies === sp;
                      const canAfford  = liveP1 >= info.cost;
                      const inv = trees[sp] ?? { total: 0 };
                      return (
                        <button key={sp} onClick={() => { setPlantSpecies(sp); setPlantMsg(''); }}
                          style={{
                            borderRadius: 12, border: `1.5px solid ${isSelected ? `rgba(${info.glow},0.8)` : `rgba(${info.glow},0.15)`}`,
                            background: isSelected ? `rgba(${info.glow},0.12)` : 'rgba(0,0,0,0.4)',
                            cursor: canAfford ? 'pointer' : 'default', textAlign: 'center', padding: '10px 6px',
                            opacity: canAfford ? 1 : 0.3,
                            boxShadow: isSelected ? `0 0 20px rgba(${info.glow},0.2)` : 'none',
                            transition: 'all 0.15s',
                          }}>
                          <img src={info.img} alt={info.label} style={{ width: 52, height: 52, objectFit: 'contain', display: 'block', margin: '0 auto 6px', filter: canAfford ? 'none' : 'grayscale(80%)' }} />
                          <div style={{ color: isSelected ? info.accent : '#9ca3af', fontSize: 13, fontWeight: 800 }}>{info.label}</div>
                          <div style={{ color: '#4b5563', fontSize: 11, marginTop: 2 }}>{info.cost} Soil · {info.maturity}</div>
                          {inv.total > 0 && <div style={{ color: info.accent, fontSize: 11, marginTop: 2, opacity: 0.7 }}>have {inv.total}</div>}
                        </button>
                      );
                    })}
                  </div>

                  {/* Quantity + plant */}
                  <div style={{ padding: '0 28px 24px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ color: '#6b7280', fontSize: 11, letterSpacing: 1, marginBottom: 6 }}>QUANTITY</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="number" min={1} max={Math.max(1, Math.min(availableSlots, maxCanPlant))} value={plantQty}
                          onChange={e => setPlantQty(Math.max(1, Math.min(Math.max(1, Math.min(availableSlots, maxCanPlant)), parseInt(e.target.value) || 1)))}
                          style={{ width: 90, padding: '10px 12px', borderRadius: 8, border: '1.5px solid rgba(52,211,153,0.4)', background: '#06120a', color: '#6ee7b7', fontSize: 18, fontWeight: 900, fontFamily: 'monospace', outline: 'none' }} />
                        <button onClick={() => setPlantQty(Math.max(1, Math.min(availableSlots, maxCanPlant)))}
                          style={{ padding: '8px 14px', borderRadius: 7, fontSize: 12, color: '#6b7280', background: 'none', border: '1px solid #374151', cursor: 'pointer' }}>Max</button>
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#6b7280', fontSize: 12 }}>
                        Total cost: <span style={{ color: '#86efac', fontWeight: 900, fontSize: 18 }}>{(plantQty * (SOIL_COST[plantSpecies] ?? 0)).toLocaleString()}</span> Soil
                        &nbsp;·&nbsp; Max plantable: <span style={{ color: '#c4b5fd', fontWeight: 700 }}>{Math.min(availableSlots, maxCanPlant)}</span>
                      </div>
                      {plantMsg && (
                        <div style={{ marginTop: 8, fontSize: 14, fontWeight: 700, color: plantMsg.startsWith('✅') ? '#6ee7b7' : '#f87171' }}>{plantMsg}</div>
                      )}
                    </div>
                    <button onClick={handlePlant} disabled={planting || maxCanPlant < 1 || availableSlots < 1}
                      style={{ padding: '14px 36px', borderRadius: 10, fontSize: 16, fontWeight: 900, cursor: (planting || maxCanPlant < 1) ? 'default' : 'pointer', background: 'linear-gradient(135deg, rgba(52,211,153,0.3), rgba(16,185,129,0.25))', border: '1.5px solid rgba(52,211,153,0.7)', color: '#6ee7b7', boxShadow: '0 0 28px rgba(52,211,153,0.2)', opacity: (planting || maxCanPlant < 1) ? 0.4 : 1, letterSpacing: 0.5 }}>
                      {planting ? '⏳ Planting…' : `Plant ${plantQty} ${SPECIES_INFO[plantSpecies]?.label} →`}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Stake NFT picker overlay ── */}
      {showStakePanel && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#0d1a0d', border: '1.5px solid rgba(52,211,153,0.4)', borderRadius: 14, width: '92%', maxWidth: 560, maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid rgba(52,211,153,0.15)' }}>
              <div>
                <div style={{ color: '#6ee7b7', fontWeight: 900, fontSize: 16, letterSpacing: 0.5 }}>
                  {previewView ? '⬡ Confirm Stake' : '⬡ Stake your Atavi NFT'}
                </div>
                <div style={{ color: '#4b5563', fontSize: 11, marginTop: 3 }}>
                  {previewView
                    ? `Atavi #${selectedNft?.template_mint} · ${selectedNft?.asset_id}`
                    : 'Select an unstaked NFT from your wallet'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {previewView && (
                  <button onClick={() => setPreviewView(false)}
                    style={{ background: 'none', border: '1px solid #374151', borderRadius: 6, color: '#6b7280', cursor: 'pointer', fontSize: 11, padding: '4px 10px' }}>← Back</button>
                )}
                <button onClick={() => { setShowStakePanel(false); setPreviewView(false); }}
                  style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>✕</button>
              </div>
            </div>

            {/* Body */}
            <div style={{ overflowY: 'auto', padding: '16px 24px', flex: 1 }}>

              {/* ── NFT PICKER ── */}
              {!previewView && <>
                {nftLoading && (
                  <div style={{ color: '#4b5563', fontSize: 13, textAlign: 'center', paddingTop: 32 }}>Loading NFTs from AtomicAssets…</div>
                )}
                {!nftLoading && nftList.length === 0 && (
                  <div style={{ color: '#4b5563', fontSize: 13, textAlign: 'center', paddingTop: 32 }}>
                    No unstaked Atavi NFTs found in <span style={{ color: '#6ee7b7' }}>{waxActor}</span>.<br />
                    <span style={{ fontSize: 11, color: '#374151', marginTop: 6, display: 'block' }}>Staked NFTs are held by kalpa.gm and not shown here.</span>
                  </div>
                )}
                {!nftLoading && nftList.map(nft => {
                  const md: Record<string, any> = nft.mutable_data ?? {};
                  const isSelected = selectedNft?.asset_id === nft.asset_id;
                  return (
                    <div key={nft.asset_id} onClick={() => setSelectedNft(isSelected ? null : nft)}
                      style={{ padding: '14px 18px', borderRadius: 10, border: `1.5px solid ${isSelected ? 'rgba(52,211,153,0.7)' : 'rgba(52,211,153,0.15)'}`, background: isSelected ? 'rgba(52,211,153,0.08)' : 'rgba(255,255,255,0.02)', marginBottom: 10, cursor: 'pointer', transition: 'all 0.15s' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ color: '#6ee7b7', fontWeight: 800, fontSize: 14 }}>Atavi #{nft.template_mint}</div>
                        <div style={{ color: '#374151', fontSize: 11, fontFamily: 'monospace' }}>ID: {nft.asset_id}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 11, color: '#4b5563' }}>Level: <span style={{ color: '#d1fae5' }}>{md.level ?? 0}</span></span>
                        <span style={{ fontSize: 11, color: '#4b5563' }}>Location: <span style={{ color: '#d1fae5' }}>{md.location || '—'}</span></span>
                        <span style={{ fontSize: 11, color: '#4b5563' }}>Soil: <span style={{ color: '#d1fae5' }}>{md.p1 ?? 0}</span></span>
                        <span style={{ fontSize: 11, color: '#4b5563' }}>Manure: <span style={{ color: '#d1fae5' }}>{md.p2 ?? 0}</span></span>
                      </div>
                    </div>
                  );
                })}
              </>}

              {/* ── PREVIEW ── */}
              {previewView && selectedNft && (() => {
                const totalRate = (plotsToLoad * 0.3).toFixed(1);
                const PARAMS = [
                  { key: 'p1', label: 'Soil',    desc: `Share of ${totalRate}/hr — set split after staking` },
                  { key: 'p2', label: 'Manure',  desc: `Share of ${totalRate}/hr — set split after staking` },
                  { key: 'p3', label: 'P3',      desc: '—' },
                  { key: 'p4', label: 'P4',      desc: '—' },
                  { key: 'p5', label: 'P5',      desc: '—' },
                  { key: 'p6', label: 'P6',      desc: '—' },
                  { key: 'p7', label: 'P7',      desc: '—' },
                  { key: 'p8', label: 'P8',      desc: '—' },
                  { key: 'p9', label: 'P9',      desc: '—' },
                  { key: 'p10',label: 'P10',     desc: '—' },
                ];
                return (
                  <div>
                    {/* Land count */}
                    <div style={{ background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.2)', borderRadius: 10, padding: '14px 18px', marginBottom: 12 }}>
                      <div style={{ color: '#9ca3af', fontSize: 11, marginBottom: 4 }}>ATAVI GLASGOW LAND IN YOUR ACCOUNT</div>
                      <div style={{ color: '#6ee7b7', fontWeight: 900, fontSize: 26 }}>{landCount.toLocaleString()} <span style={{ fontSize: 13, color: '#4b5563' }}>plots</span></div>
                    </div>

                    {/* Plots to load */}
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ color: '#9ca3af', fontSize: 11, marginBottom: 6 }}>PLOTS TO LOAD INTO THIS NFT</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <input
                          type="number" min={1} max={landCount} value={plotsToLoad}
                          onChange={e => setPlotsToLoad(Math.min(landCount, Math.max(1, parseInt(e.target.value) || 1)))}
                          style={{ width: 120, padding: '8px 12px', borderRadius: 8, border: '1.5px solid rgba(52,211,153,0.4)', background: '#0d1a0d', color: '#6ee7b7', fontSize: 15, fontWeight: 700, fontFamily: 'monospace', outline: 'none' }}
                        />
                        <button onClick={() => setPlotsToLoad(landCount)}
                          style={{ fontSize: 11, color: '#4b5563', background: 'none', border: '1px solid #374151', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>Max</button>
                        <span style={{ fontSize: 11, color: '#374151' }}>of {landCount.toLocaleString()} available</span>
                      </div>
                    </div>

                    {/* Food packets */}
                    <div style={{ background: 'rgba(251,191,36,0.05)', border: '1px solid rgba(251,191,36,0.15)', borderRadius: 10, padding: '14px 18px', marginBottom: 14 }}>
                      <div style={{ color: '#9ca3af', fontSize: 11, marginBottom: 4 }}>FOOD SCORE IN YOUR ACCOUNT</div>
                      <div style={{ color: '#fbbf24', fontWeight: 900, fontSize: 22, marginBottom: 10 }}>{foodScore.toFixed(1)} <span style={{ fontSize: 12, color: '#4b5563' }}>food · {maxPackets} packets available (÷10.1)</span></div>
                      <div style={{ color: '#9ca3af', fontSize: 11, marginBottom: 6 }}>FOOD PACKETS TO LOAD INTO THIS NFT</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <input
                          type="number" min={0} max={maxPackets} value={packetsToLoad}
                          onChange={e => setPacketsToLoad(Math.min(maxPackets, Math.max(0, parseInt(e.target.value) || 0)))}
                          style={{ width: 120, padding: '8px 12px', borderRadius: 8, border: '1.5px solid rgba(251,191,36,0.4)', background: '#0d1a0d', color: '#fbbf24', fontSize: 15, fontWeight: 700, fontFamily: 'monospace', outline: 'none' }}
                        />
                        <button onClick={() => setPacketsToLoad(maxPackets)}
                          style={{ fontSize: 11, color: '#4b5563', background: 'none', border: '1px solid #374151', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>Max</button>
                        <button onClick={() => setPacketsToLoad(0)}
                          style={{ fontSize: 11, color: '#4b5563', background: 'none', border: '1px solid #374151', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>None</button>
                      </div>
                    </div>

                    {/* What changes */}
                    <div style={{ color: '#9ca3af', fontSize: 11, marginBottom: 8 }}>WHAT WILL BE LOADED ONTO THE NFT</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {PARAMS.map(p => (
                        <div key={p.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', borderRadius: 8, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(52,211,153,0.08)' }}>
                          <span style={{ color: '#6ee7b7', fontSize: 12, fontWeight: 700, width: 52 }}>{p.label}</span>
                          <span style={{ color: '#4b5563', fontSize: 11, flex: 1 }}>{p.desc}</span>
                          <span style={{ color: '#d1fae5', fontSize: 11, fontFamily: 'monospace' }}>{(selectedNft.mutable_data ?? {})[p.key] ?? 0}</span>
                        </div>
                      ))}
                    </div>

                    <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 8, background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)' }}>
                      <div style={{ color: '#a5b4fc', fontSize: 11, fontWeight: 700, marginBottom: 4 }}>ℹ Soil / Manure Split</div>
                      <span style={{ color: '#6b7280', fontSize: 11, lineHeight: 1.6 }}>
                        Total accrual rate is <span style={{ color: '#a5b4fc' }}>{totalRate}/hr</span> from your {plotsToLoad.toLocaleString()} plots.
                        After staking you set a percentage split between Soil and Manure — e.g. 30% Soil / 70% Manure.
                        Only one grows at any moment based on that split. You can change the split at any time.
                      </span>
                    </div>

                    <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 8, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)' }}>
                      <span style={{ color: '#fbbf24', fontSize: 11 }}>
                        ⚠ Once staked, the NFT will be held in kalpa.gm. You can unstake it anytime — the NFT returns to your wallet with all its features intact.
                      </span>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Footer */}
            {selectedNft && !previewView && (
              <div style={{ padding: '14px 24px', borderTop: '1px solid rgba(52,211,153,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, color: '#6ee7b7' }}>Selected: Atavi #{selectedNft.template_mint} · {selectedNft.asset_id}</span>
                <button onClick={loadPreview} disabled={previewLoading}
                  style={{ padding: '9px 24px', borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: previewLoading ? 'default' : 'pointer', background: 'linear-gradient(135deg, rgba(52,211,153,0.2), rgba(16,185,129,0.15))', border: '1.5px solid rgba(52,211,153,0.6)', color: '#6ee7b7' }}
                >{previewLoading ? 'Loading…' : 'Load Land Preview →'}</button>
              </div>
            )}
            {selectedNft && previewView && (
              <div style={{ padding: '14px 24px', borderTop: '1px solid rgba(52,211,153,0.15)' }}>
                {stakeMsg && (
                  <div style={{ marginBottom: 10, fontSize: 12, fontWeight: 700, color: stakeMsg.startsWith('✅') ? '#6ee7b7' : stakeMsg.startsWith('❌') ? '#f87171' : '#fbbf24' }}>{stakeMsg}</div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 12, color: '#4b5563' }}>{plotsToLoad.toLocaleString()} plots · {packetsToLoad} packets → Atavi #{selectedNft.template_mint}</span>
                  {stakeMsg.startsWith('✅')
                    ? <button onClick={() => { setShowStakePanel(false); setPreviewView(false); setStakeMsg(''); }}
                        style={{ padding: '10px 28px', borderRadius: 8, fontSize: 14, fontWeight: 900, cursor: 'pointer', background: 'linear-gradient(135deg, rgba(52,211,153,0.25), rgba(16,185,129,0.2))', border: '1.5px solid rgba(52,211,153,0.7)', color: '#6ee7b7', letterSpacing: 0.5 }}
                      >Close ✓</button>
                    : <button onClick={handleStake} disabled={staking}
                        style={{ padding: '10px 28px', borderRadius: 8, fontSize: 14, fontWeight: 900, cursor: staking ? 'default' : 'pointer', background: 'linear-gradient(135deg, rgba(52,211,153,0.25), rgba(16,185,129,0.2))', border: '1.5px solid rgba(52,211,153,0.7)', color: '#6ee7b7', letterSpacing: 0.5, opacity: staking ? 0.6 : 1 }}
                      >{staking ? '⏳ Staking…' : 'Accept & Stake →'}</button>
                  }
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AtaviForestPage;
